import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';

const CATEGORY_LABELS = {
  founder: 'Founder',
  editor: 'Editor',
  reviewer: 'Reviewer',
};

const CATEGORY_COLORS = {
  founder: { 
    bg: 'bg-pink-500', 
    badge: 'bg-pink-100 text-pink-600 border-pink-200',
    card: 'border-pink-200 bg-pink-50/40' // 🔥 subtle premium look
  },
  editor: { 
    bg: 'bg-[#1A73E8]', 
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    card: ''
  },
  reviewer: { 
    bg: 'bg-[#174EA6]', 
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    card: ''
  },
};

const AcademicBoardCard = ({ member, isAdmin = false, onEdit, onDelete }) => {
  const initials = member.name
    .split(' ')
    .filter(n => !['Prof.', 'Dr.', 'Prof'].includes(n))
    .slice(0, 2)
    .map(n => n[0])
    .join('');

  const category = member.category || 'reviewer';
  const colors = CATEGORY_COLORS[category];

  return (
    <div
      className={`bg-white border p-4 flex flex-col gap-3 transition-all duration-200 hover:shadow-sm ${colors.card || 'border-[#E5E7EB]'}`}
      data-testid={`board-member-${member.id}`}
    >
      {/* Member info */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-10 h-10 ${colors.bg} flex items-center justify-center flex-shrink-0`}>
            <span className="text-white font-semibold text-sm">{initials}</span>
          </div>

          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-[#1F1F1F] text-sm leading-snug">
              {member.name}
            </h4>

            {member.designation && (
              <p className="text-[#6B7280] text-xs mt-0.5 leading-snug">
                {member.designation}
              </p>
            )}

            {!member.designation && member.title && (
              <p className="text-[#6B7280] text-xs mt-0.5 leading-snug">
                {member.title}
              </p>
            )}

            <p className="text-[#4B5563] text-xs mt-1 leading-snug">
              {member.institution}
            </p>
          </div>
        </div>
      </div>

      {/* Badge + Role */}
      <div className="flex items-center justify-between pt-2 border-t border-[#E5E7EB]">
        <span className={`text-xs px-2 py-0.5 border font-medium ${colors.badge}`}>
          {CATEGORY_LABELS[category] || 'Member'}
        </span>

        <span className="text-xs text-[#6B7280] text-right max-w-[110px] leading-tight">
          {member.role}
        </span>
      </div>

      {/* Admin controls */}
      {isAdmin && (
        <div className="flex gap-2 pt-2 border-t border-[#E5E7EB]">
          <button
            onClick={() => onEdit && onEdit(member)}
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 border border-[#E5E7EB] text-xs text-[#374151] hover:bg-[#F5F7FA] hover:border-[#1A73E8] hover:text-[#1A73E8] transition-colors"
          >
            <Pencil size={11} /> Edit
          </button>

          <button
            onClick={() => onDelete && onDelete(member)}
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 border border-[#E5E7EB] text-xs text-[#374151] hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors"
          >
            <Trash2 size={11} /> Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default AcademicBoardCard;
